import { supabase } from '../../utils/supabaseClient';

/**
 * Get the next invoice number for a user
 * @param {string} userId - User ID
 * @returns {Promise<string>} - Next invoice number
 */
export async function getNextInvoiceNo(userId, isNonGst = false) {
  const prefix = isNonGst ? 'NG-' : '';

  const { data, error } = await supabase
    .from('invoices')
    .select('invoice_no')
    .eq('user_id', userId)
    .ilike('invoice_no', isNonGst ? 'NG-%' : '%') // Filter based on prefix
    .order('invoice_no', { ascending: false });

  if (error) throw error;

  // Filter in memory to be sure, as ilike '%' matches everything including NG-
  const filteredData = data?.filter(d => {
    if (isNonGst) {
      return d.invoice_no && d.invoice_no.startsWith('NG-');
    } else {
      // For regular invoices, exclude those starting with NG-
      return d.invoice_no && !d.invoice_no.startsWith('NG-');
    }
  }) || [];

  if (filteredData.length === 0) {
    return isNonGst ? 'NG-001' : '001';
  }

  // Find the max number from the filtered list
  let maxNum = 0;
  for (const inv of filteredData) {
    // Extract numeric part
    // For NG-005 -> 5
    // For 005 -> 5
    // For INV/25-26/005 -> 5
    const match = inv.invoice_no.match(/(\d+)$/);
    if (match) {
      const num = parseInt(match[1], 10);
      if (num > maxNum) maxNum = num;
    }
  }

  const next = maxNum + 1;
  const listNum = next.toString().padStart(3, '0');

  return isNonGst ? `NG-${listNum}` : listNum;
}

/**
 * Create a new invoice with items
 * @param {string} userId - User ID
 * @param {Object} invoice - Invoice data
 * @param {Array} items - Invoice items
 * @returns {Promise<Object>} - Created invoice
 */
export async function createInvoice(userId, invoice, items) {
  const toNumber = (v, d = 0) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : d;
  };

  // Extract GST calculation details
  const gstCalc = invoice.gstCalculation || {};
  const gstType = gstCalc.gstType || 'IGST';
  const isSameState = gstType === 'CGST+SGST';

  const invoicePayload = {
    user_id: userId,
    customer_id: invoice.customerId,
    invoice_no: invoice.invoiceNo,
    invoice_date: invoice.invoiceDate,
    from_date: invoice.fromDate,
    to_date: invoice.toDate,
    subtotal: toNumber(invoice.subtotal),
    // store computed amounts
    fuel_charge: toNumber(invoice.fuelCharge),
    royalty_charge: toNumber(invoice.royaltyCharge),
    docket_charge: toNumber(invoice.docketCharge),
    oda_charge: toNumber(invoice.odaCharge),
    other_charge: toNumber(invoice.otherCharge),
    total_before_gst: toNumber(invoice.totalBeforeGST),
    // GST summary columns (schema-agnostic)
    gst_percent: toNumber(invoice.gstCalculation?.gstPercent ?? invoice.gstPercent, 0),
    gst_amount: toNumber(invoice.gstCalculation?.totalGST ?? invoice.gstAmount, 0),
    grand_total: toNumber(invoice.grandTotal),
    // store entered percentages separately if provided
    fuel_percent: toNumber(invoice.fuelPercent, 0),
    royalty_percent: toNumber(invoice.royaltyPercent, 0),
    // store detailed GST breakdown
    gst_type: gstType,
    cgst_percent: isSameState ? toNumber(gstCalc.cgstPercent, 0) : 0,
    cgst_amount: isSameState ? toNumber(gstCalc.cgstAmount, 0) : 0,
    sgst_percent: isSameState ? toNumber(gstCalc.sgstPercent, 0) : 0,
    sgst_amount: isSameState ? toNumber(gstCalc.sgstAmount, 0) : 0,
    igst_percent: !isSameState ? toNumber(gstCalc.igstPercent, 0) : 0,
    igst_amount: !isSameState ? toNumber(gstCalc.igstAmount, 0) : 0,
  };
  // If gst_amount not provided, compute from percent and total_before_gst
  if (!invoicePayload.gst_amount && invoicePayload.gst_percent) {
    invoicePayload.gst_amount = (invoicePayload.total_before_gst * invoicePayload.gst_percent) / 100;
    invoicePayload.grand_total = invoicePayload.total_before_gst + invoicePayload.gst_amount;
  }

  const { data: inserted, error: insertError } = await supabase
    .from('invoices')
    .insert(invoicePayload)
    .select('*')
    .single();
  if (insertError) throw insertError;

  const invoiceId = inserted.id;
  if (items && items.length > 0) {
    const itemsPayload = items.map((b, idx) => ({
      user_id: userId,
      invoice_id: invoiceId,
      booking_id: b.id || null,
      line_no: idx + 1,
      date: b.date,
      awb_no: b.awb_no || b.awbNo,
      receiver_name: b.receiver_name || b.receiverName,
      receiver_address: b.receiver_address || b.receiverAddress || null,
      mode: b.mode,
      type: b.type || null,
      weight: toNumber(b.weight, 0),
      quantity: parseInt(b.quantity || 1, 10),
      amount: toNumber(b.amount, 0),
      other_charge: toNumber(b.other_charge, 0),
    }));

    const { error: itemsError } = await supabase
      .from('invoice_items')
      .insert(itemsPayload);
    if (itemsError) throw itemsError;
  }

  return inserted;
}

/**
 * List all invoices for a user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} - Array of invoices
 */
export async function listInvoices(userId) {
  const { data, error } = await supabase
    .from('invoices')
    .select('*, customers(customer_name, customer_state)')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

/**
 * Get recent invoices for a user
 * @param {string} userId - User ID
 * @param {number} limit - Maximum number of invoices to return
 * @returns {Promise<Array>} - Array of recent invoices
 */
export async function getRecentInvoices(userId, limit = 5) {
  const { data, error } = await supabase
    .from('invoices')
    .select('*, customers(customer_name)')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;

  // Format the data to include customer_name directly in the invoice object
  return (data || []).map(invoice => ({
    ...invoice,
    customer_name: invoice.customers?.customer_name || 'Unknown'
  }));
}

/**
 * Get invoice items for a specific invoice
 * @param {string} userId - User ID
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise<Array>} - Array of invoice items
 */
export async function getInvoiceItems(userId, invoiceId) {
  const { data, error } = await supabase
    .from('invoice_items')
    .select('*')
    .eq('user_id', userId)
    .eq('invoice_id', invoiceId)
    .order('line_no', { ascending: true });
  if (error) throw error;
  return data || [];
}

/**
 * Delete an invoice
 * @param {string} userId - User ID
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise<void>}
 */
export async function deleteInvoice(userId, invoiceId) {
  const { error } = await supabase
    .from('invoices')
    .delete()
    .eq('user_id', userId)
    .eq('id', invoiceId);
  if (error) throw error;
}

export async function getInvoice(userId, invoiceId) {
  const { data, error } = await supabase
    .from('invoices')
    .select('*, customers(customer_name, customer_id, address, gst_no, customer_state)')
    .eq('user_id', userId)
    .eq('id', invoiceId)
    .single();
  if (error) throw error;
  return data;
}

export async function updateInvoice(userId, invoiceId, invoice, items) {
  const toNumber = (v, d = 0) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : d;
  };

  // Extract GST calculation details
  const gstCalc = invoice.gstCalculation || {};
  const gstType = gstCalc.gstType || 'IGST';
  const isSameState = gstType === 'CGST+SGST';

  const invoicePayload = {
    customer_id: invoice.customerId,
    invoice_date: invoice.invoiceDate,
    from_date: invoice.fromDate,
    to_date: invoice.toDate,
    subtotal: toNumber(invoice.subtotal),
    // computed amounts
    fuel_charge: toNumber(invoice.fuelCharge),
    royalty_charge: toNumber(invoice.royaltyCharge),
    docket_charge: toNumber(invoice.docketCharge),
    oda_charge: toNumber(invoice.odaCharge),
    other_charge: toNumber(invoice.otherCharge),
    total_before_gst: toNumber(invoice.totalBeforeGST),
    // GST summary columns only (to avoid missing columns)
    gst_percent: toNumber(invoice.gstCalculation?.gstPercent ?? invoice.gstPercent, 0),
    gst_amount: toNumber(invoice.gstCalculation?.totalGST ?? invoice.gstAmount, 0),
    grand_total: toNumber(invoice.grandTotal),
    // store entered percentages
    fuel_percent: toNumber(invoice.fuelPercent, 0),
    royalty_percent: toNumber(invoice.royaltyPercent, 0),
    // store detailed GST breakdown
    gst_type: gstType,
    cgst_percent: isSameState ? toNumber(gstCalc.cgstPercent, 0) : 0,
    cgst_amount: isSameState ? toNumber(gstCalc.cgstAmount, 0) : 0,
    sgst_percent: isSameState ? toNumber(gstCalc.sgstPercent, 0) : 0,
    sgst_amount: isSameState ? toNumber(gstCalc.sgstAmount, 0) : 0,
    igst_percent: !isSameState ? toNumber(gstCalc.igstPercent, 0) : 0,
    igst_amount: !isSameState ? toNumber(gstCalc.igstAmount, 0) : 0,
  };
  if (!invoicePayload.gst_amount && invoicePayload.gst_percent) {
    invoicePayload.gst_amount = (invoicePayload.total_before_gst * invoicePayload.gst_percent) / 100;
    invoicePayload.grand_total = invoicePayload.total_before_gst + invoicePayload.gst_amount;
  }

  const { data, error } = await supabase
    .from('invoices')
    .update(invoicePayload)
    .eq('user_id', userId)
    .eq('id', invoiceId)
    .select('*')
    .single();
  if (error) throw error;

  // Update items if provided
  if (items && items.length > 0) {
    // First delete existing items
    const { error: deleteError } = await supabase
      .from('invoice_items')
      .delete()
      .eq('user_id', userId)
      .eq('invoice_id', invoiceId);
    if (deleteError) throw deleteError;

    // Then insert new items
    const itemsPayload = items.map((b, idx) => ({
      user_id: userId,
      invoice_id: invoiceId,
      booking_id: b.id || null,
      line_no: idx + 1,
      date: b.date,
      awb_no: b.awb_no || b.awbNo,
      receiver_name: b.receiver_name || b.receiverName,
      receiver_address: b.receiver_address || b.receiverAddress || null,
      mode: b.mode,
      type: b.type || null,
      weight: toNumber(b.weight, 0),
      quantity: parseInt(b.quantity || 1, 10),
      amount: toNumber(b.amount, 0),
      other_charge: toNumber(b.other_charge, 0),
    }));

    const { error: itemsError } = await supabase
      .from('invoice_items')
      .insert(itemsPayload);
    if (itemsError) throw itemsError;
  }

  return data;
}


